import React, { createContext, useContext, useState, useEffect } from 'react';
import { PortfolioService } from '../services/PortfolioService';
import { NotificationService, INotification, NotificationType } from '../services/NotificationService';
import { Project, TargetRole } from '../domain/Project';
import { Skill } from '../domain/Skill';
import { Experience } from '../domain/Experience';

interface IPortfolioContext {
  activeRole: TargetRole;
  setActiveRole: (role: TargetRole) => void;
  selectedCategory: string;
  setSelectedCategory: (cat: string) => void;
  projects: Project[];
  skills: Skill[];
  experiences: Experience[];
  selectedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
  isResumeModalOpen: boolean;
  setIsResumeModalOpen: (open: boolean) => void;
  notifications: INotification[];
  notify: (msg: string, type?: NotificationType) => void;
  theme: 'dark' | 'light';
  toggleTheme: () => void;
}

const PortfolioContext = createContext<IPortfolioContext | undefined>(undefined);

export const PortfolioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const portfolioService = PortfolioService.getInstance();
  const notificationService = NotificationService.getInstance();

  const [activeRole, setActiveRole] = useState<TargetRole>('web-developer');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    return (localStorage.getItem('theme') as 'dark' | 'light') || 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  useEffect(() => {
    const unsubscribe = notificationService.subscribe(setNotifications);
    return () => unsubscribe();
  }, [notificationService]);

  const projects = portfolioService.getProjects(activeRole, selectedCategory);
  const skills = portfolioService.getSkills(activeRole);
  const experiences = portfolioService.getExperiences();

  const notify = (msg: string, type: NotificationType = 'success') => {
    notificationService.show(msg, type);
  };

  return (
    <PortfolioContext.Provider
      value={{
        activeRole,
        setActiveRole,
        selectedCategory,
        setSelectedCategory,
        projects,
        skills,
        experiences,
        selectedProject,
        setSelectedProject,
        isResumeModalOpen,
        setIsResumeModalOpen,
        notifications,
        notify,
        theme,
        toggleTheme
      }}
    >
      {children}
    </PortfolioContext.Provider>
  );
};

export const usePortfolio = (): IPortfolioContext => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};
